import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Tabs,
    Tab,
    TablePagination
} from '@mui/material';
import Navbar from '../../globals/components/Navbar';
import { listSupportRequests, resumeSupportRequest, updateSupportRequest, deleteSupportRequest } from '../../services/supportRequestService';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { apiClient } from '../../globals/utils/api';
import TablePaginationActions from "../../globals/components/TablePaginationActions";

const SupportRequestsLog = () => {
    const { role } = useSelector((state) => state.auth);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [adminComment, setAdminComment] = useState("");
    const navigate = useNavigate();
    const fetchInProgress = React.useRef(false);
    const [activeTab, setActiveTab] = useState(0); // 0 = Open, 1 = Completed
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const isAuthorized = role === 'super_user' || role === 'internal_user';

    useEffect(() => {
        if (!isAuthorized) {
            navigate('/');
            toast.error("Access Denied: You do not have permission to view this page.");
            return;
        }

        if (role === 'internal_user') {
            const checkFeature = async () => {
                try {
                    const state = await import('../../store/store').then(m => m.store.getState());
                    const authUser = state.auth.user;
                    if (authUser?.role_id) {
                        const response = await apiClient(`/get-internal-user/${authUser.role_id}`, "GET");
                        const features = response.features || [];
                        if (!features.includes('support_requests')) {
                            toast.error("You do not have permission to view support requests.");
                            navigate('/');
                        }
                    }
                } catch (e) {
                    console.error("Perm check failed", e);
                }
            };
            checkFeature();
        }

        if (!fetchInProgress.current) {
            fetchRequests();
        }
    }, [isAuthorized, navigate, role]);

    const fetchRequests = async () => {
        if (fetchInProgress.current) return;
        fetchInProgress.current = true;
        setLoading(true);
        try {
            const data = await listSupportRequests();
            // Ensure latest-first (newest created_at on top)
            const sorted = Array.isArray(data)
                ? [...data].sort((a, b) => {
                    const aTime = new Date(a?.created_at || 0).getTime();
                    const bTime = new Date(b?.created_at || 0).getTime();
                    return bTime - aTime;
                })
                : [];
            setRequests(sorted);
        } catch (error) {
            console.error("Failed to fetch support requests:", error);
            // Only toast if it's the first failure to avoid flooding
            toast.error("Failed to load support requests");
        } finally {
            setLoading(false);
            fetchInProgress.current = false;
        }
    };

    const tabFilteredRequests = useMemo(() => {
        const isCompletedTab = activeTab === 1;
        return (requests || []).filter((req) => {
            const status = String(req?.status || "").toLowerCase();
            const isCompleted = status === "completed";
            return isCompletedTab ? isCompleted : !isCompleted;
        });
    }, [requests, activeTab]);

    const openCount = useMemo(
        () => (requests || []).filter((r) => String(r?.status || "").toLowerCase() !== "completed").length,
        [requests]
    );
    const completedCount = useMemo(
        () => (requests || []).filter((r) => String(r?.status || "").toLowerCase() === "completed").length,
        [requests]
    );

    const paginatedRequests = useMemo(() => {
        const start = page * rowsPerPage;
        return tabFilteredRequests.slice(start, start + rowsPerPage);
    }, [tabFilteredRequests, page, rowsPerPage]);

    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
        setPage(0); // Reset pagination when switching tabs
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const next = parseInt(event.target.value, 10);
        setRowsPerPage(next);
        setPage(0);
    };

    const DESCRIPTION_PREVIEW_LIMIT = 120;
    const truncateText = (text, limit) => {
        const s = String(text || "");
        if (!s) return "";
        if (s.length <= limit) return s;
        return `${s.slice(0, limit).trimEnd()}…`;
    };

    const handleView = (req) => {
        setSelectedRequest(req);
        setAdminComment(req.admin_comment || "");
        setDetailOpen(true);
    };

    const handleClose = () => {
        setDetailOpen(false);
        setSelectedRequest(null);
    };

    const handleResume = async (req) => {
        try {
            const payload = await resumeSupportRequest(req.support_request_id);
            // Payload from backend: { bond_cache_id, request_body, bond_type, name }

            localStorage.setItem('resumedBondPayload', JSON.stringify(payload));

            // Determine route based on bond type
            let route = '/vermost-probate-bond-request';
            const type = (payload.bond_type || req.bond_type || "").toLowerCase();

            if (type.includes('probate')) {
                route = '/vermost-probate-bond-request';
            } else if (type.includes('guardianship')) {
                route = '/vermost-guardianship-bond-request'; // Assuming route
            }
            // Logic defaults to probate if unknown, can be improved.

            navigate(route);
            toast.info("Resuming bond application...");
        } catch (error) {
            console.error("Failed to resume", error);
            // Check if error is 403 (Permission) or 404
            toast.error("Failed to fetch bond data. Check permissions.");
        }
    };

    const handleMarkComplete = async () => {
        if (!selectedRequest) return;
        try {
            await updateSupportRequest(selectedRequest.support_request_id, {
                internal_comment: adminComment
            });
            toast.success("Request marked as completed");
            handleClose();
            window.location.reload();
        } catch (error) {
            console.error("Failed to update request", error);
            toast.error("Failed to update request");
        }
    };

    const handleDelete = async (req) => {
        if (!window.confirm(`Are you sure you want to delete support request #${req.support_request_id}? This will also delete the cached bond data.`)) {
            return;
        }
        try {
            await deleteSupportRequest(req.support_request_id);
            toast.success("Request deleted successfully");
            fetchRequests();
        } catch (error) {
            console.error("Failed to delete request", error);
            toast.error("Failed to delete request");
        }
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 4, maxWidth: '100%', mx: 'auto' }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    Support Requests Log
                </Typography>
                <Paper
                    elevation={0}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper'
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                minHeight: 64,
                                fontSize: '1rem',
                            },
                            '& .MuiTabs-indicator': {
                                height: 3,
                            },
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>Open</span>
                                    <Chip size="small" label={openCount} color="warning" />
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>Completed</span>
                                    <Chip size="small" label={completedCount} color="success" />
                                </Box>
                            }
                        />
                    </Tabs>
                </Paper>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Bond Type</strong></TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Requester Name</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                {activeTab === 1 && <TableCell><strong>By</strong></TableCell>}
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : tabFilteredRequests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {activeTab === 0 ? "No open support requests found" : "No completed support requests found"}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedRequests.map((req) => (
                                    <TableRow key={req.support_request_id}>
                                        <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>{req.bond_type}</TableCell>
                                        <TableCell>{req.name || "N/A"}</TableCell>
                                        <TableCell>{req.requester_name || req.requester_id}</TableCell>
                                        <TableCell sx={{ maxWidth: 360 }}>
                                            <Tooltip title={req.user_comment || ""} placement="top" arrow>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {truncateText(req.user_comment, DESCRIPTION_PREVIEW_LIMIT)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Current Status">
                                                <Chip
                                                    label={req.status}
                                                    color={req.status === 'completed' ? 'success' : 'warning'}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600,
                                                        textTransform: 'capitalize'
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        {activeTab === 1 && (
                                            <TableCell>
                                                {req.status === 'completed' ? (
                                                    <Box>
                                                        <Typography variant="caption" display="block" fontWeight="bold">
                                                            {req.completed_by_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {new Date(req.updated_at).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="textSecondary">-</Typography>
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell align="center">
                                            <Tooltip title="View Details">
                                                <IconButton onClick={() => handleView(req)} color="primary">
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Request">
                                                <IconButton onClick={() => handleDelete(req)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
          ActionsComponent={TablePaginationActions}
                    component="div"
                    count={tabFilteredRequests.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />

                {/* Detail Dialog */}
                <Dialog open={detailOpen} onClose={handleClose} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ bgcolor: '#f5f5f5' }}>
                        Support Request #{selectedRequest?.support_request_id}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ display: 'grid', gap: 3, pt: 1 }}>
                            <Box display="flex" gap={2}>
                                <Typography variant="body2" color="textSecondary"><strong>Bond Type:</strong> {selectedRequest?.bond_type}</Typography>
                                <Typography variant="body2" color="textSecondary"><strong>Date:</strong> {selectedRequest && new Date(selectedRequest.created_at).toLocaleString()}</Typography>
                            </Box>
                            {selectedRequest?.status === 'completed' && selectedRequest?.completed_by_name && (
                                <Typography variant="body2" color="success.main">
                                    <strong>Completed By:</strong> {selectedRequest.completed_by_name}
                                </Typography>
                            )}

                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>User Description</Typography>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff9f9' }}>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedRequest?.user_comment}
                                    </Typography>
                                </Paper>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Admin Internal Notes</Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={adminComment}
                                    onChange={(e) => setAdminComment(e.target.value)}
                                    placeholder="Add notes about resolution..."
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                        <Box>
                            {selectedRequest?.status !== 'completed' && (
                                <Button
                                    onClick={() => handleResume(selectedRequest)}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<VisibilityIcon />}
                                >
                                    View Bond Request
                                </Button>
                            )}
                        </Box>
                        <Box>
                            <Button onClick={handleClose} sx={{ mr: 1 }}>Close</Button>
                            {selectedRequest?.status !== 'completed' && (
                                <Button
                                    onClick={handleMarkComplete}
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleIcon />}
                                >
                                    Mark as Completed
                                </Button>
                            )}
                        </Box>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default SupportRequestsLog;
